declare const gapi: any;

export type BatchUpdateEntry = {
  row: number;
  col: number;
  value: string;
};

// New types for extended functionality
export interface PermissionCheckResult {
  spreadsheetId: string;
  hasAccess: boolean;
  canRead: boolean;
  canWrite: boolean;
  error?: string;
}

export interface MultiSheetConfig {
  spreadsheetId: string;
  sheetName: string;
  range?: string;
}

export interface MultiSheetResult {
  spreadsheetId: string;
  sheetName: string;
  values: string[][];
  error?: string;
}

export interface SheetMetadata {
  spreadsheetId: string;
  title: string;
  lastModified: string;
  sheetCount: number;
  sheets: Array<{
    sheetId: number;
    title: string;
    index: number;
  }>;
}

export interface BatchUpdateMultipleResult {
  spreadsheetId: string;
  success: boolean;
  updatedCells: number;
  error?: string;
}

interface PermissionCacheEntry {
  result: PermissionCheckResult;
  timestamp: number;
  ttl: number;
}

class GoogleSheetsService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private accessToken: string | null = null;
  private permissionCache = new Map<string, PermissionCacheEntry>();
  private readonly PERMISSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 1000; // 1 second

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (typeof gapi === 'undefined') {
      throw new Error('Google API script failed to load.');
    }

    this.initPromise = new Promise((resolve, reject) => {
      gapi.load('client', async () => {
        try {
          await gapi.client.init({
            discoveryDocs: ['https://sheets.googleapis.com/$discovery/rest?version=v4']
          });
          this.initialized = true;
          if (this.accessToken) {
            gapi.client.setToken({ access_token: this.accessToken });
          }
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });

    return this.initPromise;
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
    if (typeof gapi !== 'undefined' && gapi?.client) {
      gapi.client.setToken(token ? { access_token: token } : null);
    }
  }

  private ensureAuthorized() {
    if (!this.accessToken) {
      throw new Error('Google authorization required. Please sign in.');
    }
  }

  private formatRange(sheetName: string, range = 'A:Z'): string {
    if (/[^A-Za-z0-9_]/.test(sheetName)) {
      const escaped = sheetName.replace(/'/g, "''");
      return `'${escaped}'!${range}`;
    }
    return `${sheetName}!${range}`;
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryOperation<T>(
    operation: () => Promise<T>,
    maxAttempts: number = this.MAX_RETRY_ATTEMPTS
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // Don't retry permission errors (403) or not found errors (404)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if (status === 403 || status === 404) {
            throw error;
          }
        }

        if (attempt < maxAttempts) {
          await this.sleep(this.RETRY_DELAY * attempt);
        }
      }
    }

    throw lastError!;
  }

  private isPermissionError(error: any): boolean {
    return error &&
           typeof error === 'object' &&
           ('status' in error && error.status === 403) ||
           ('code' in error && error.code === 403);
  }

  private getCacheKey(spreadsheetId: string): string {
    return `permission_${spreadsheetId}`;
  }

  private isCacheValid(entry: PermissionCacheEntry): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  async getSheetTitle(spreadsheetId: string, sheetId?: number): Promise<string> {
    await this.initialize();
    this.ensureAuthorized();

    const response = await gapi.client.sheets.spreadsheets.get({
      spreadsheetId,
      fields: 'sheets(properties(sheetId,title))'
    });

    const sheets = (response.result?.sheets ?? []).map((sheet: any) => sheet.properties);
    if (sheetId !== undefined) {
      const match = sheets.find((sheet: any) => sheet.sheetId === sheetId);
      if (match?.title) {
        return match.title as string;
      }
    }

    const firstSheet = sheets[0];
    if (!firstSheet?.title) {
      throw new Error('Unable to resolve sheet title.');
    }
    return firstSheet.title as string;
  }

  async getSheetValues(spreadsheetId: string, sheetName: string, range = 'A:Z'): Promise<string[][]> {
    await this.initialize();
    this.ensureAuthorized();

    const response = await gapi.client.sheets.spreadsheets.values.get({
      spreadsheetId,
      range: this.formatRange(sheetName, range)
    });

    return response.result?.values ?? [];
  }

  // Legacy method - no longer used
  async getSheetData(_spreadsheetId?: string, _sheetName?: string, _range?: string): Promise<string> {
    console.warn('getSheetData() is deprecated and no longer used');
    return '';
  }

  async updateCellInSheet(spreadsheetId: string, sheetName: string, row: number, col: number, value: string): Promise<void> {
    await this.initialize();
    this.ensureAuthorized();

    const columnLetters: string[] = [];
    let current = col;
    do {
      columnLetters.unshift(String.fromCharCode(65 + (current % 26)));
      current = Math.floor(current / 26) - 1;
    } while (current >= 0);

    const range = this.formatRange(sheetName, `${columnLetters.join('')}${row + 1}`);

    await gapi.client.sheets.spreadsheets.values.update({
      spreadsheetId,
      range,
      valueInputOption: 'USER_ENTERED',
      resource: {
        values: [[value]]
      }
    });
  }

  // === NEW PERMISSION CHECKING METHODS ===

  /**
   * Check if user can access a specific spreadsheet
   */
  async checkSheetAccess(spreadsheetId: string): Promise<PermissionCheckResult> {
    await this.initialize();
    this.ensureAuthorized();

    // Check cache first
    const cacheKey = this.getCacheKey(spreadsheetId);
    const cached = this.permissionCache.get(cacheKey);
    if (cached && this.isCacheValid(cached)) {
      return cached.result;
    }

    const result: PermissionCheckResult = {
      spreadsheetId,
      hasAccess: false,
      canRead: false,
      canWrite: false
    };

    try {
      // Test read access by trying to get spreadsheet metadata
      await this.retryOperation(async () => {
        const response = await gapi.client.sheets.spreadsheets.get({
          spreadsheetId,
          fields: 'properties(title)'
        });
        return response;
      });

      result.hasAccess = true;
      result.canRead = true;

      // Test write access by attempting a no-op batch update
      try {
        await this.retryOperation(async () => {
          await gapi.client.sheets.spreadsheets.batchUpdate({
            spreadsheetId,
            resource: {
              requests: [] // Empty request array - no actual changes
            }
          });
        });
        result.canWrite = true;
      } catch (writeError) {
        if (!this.isPermissionError(writeError)) {
          // If it's not a permission error, assume we have write access
          result.canWrite = true;
        }
      }

    } catch (error) {
      result.error = error instanceof Error ? error.message : 'Unknown error';
      if (this.isPermissionError(error)) {
        result.error = 'Access denied - insufficient permissions';
      }
    }

    // Cache the result
    this.permissionCache.set(cacheKey, {
      result,
      timestamp: Date.now(),
      ttl: this.PERMISSION_CACHE_TTL
    });

    return result;
  }

  /**
   * Check access for multiple spreadsheets in parallel
   */
  async checkMultipleSheetAccess(spreadsheetIds: string[]): Promise<PermissionCheckResult[]> {
    const promises = spreadsheetIds.map(id => this.checkSheetAccess(id));
    return Promise.all(promises);
  }

  // === NEW MULTI-SHEET OPERATIONS ===

  /**
   * Fetch data from multiple sheets in parallel
   */
  async getMultipleSheetValues(configs: MultiSheetConfig[]): Promise<MultiSheetResult[]> {
    await this.initialize();
    this.ensureAuthorized();

    const promises = configs.map(async (config): Promise<MultiSheetResult> => {
      try {
        const values = await this.retryOperation(async () => {
          return this.getSheetValues(config.spreadsheetId, config.sheetName, config.range);
        });

        return {
          spreadsheetId: config.spreadsheetId,
          sheetName: config.sheetName,
          values
        };
      } catch (error) {
        return {
          spreadsheetId: config.spreadsheetId,
          sheetName: config.sheetName,
          values: [],
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    });

    return Promise.all(promises);
  }

  /**
   * Update multiple sheets with proper error handling per sheet
   */
  async batchUpdateMultipleSheets(updates: Array<{
    spreadsheetId: string;
    sheetName: string;
    updates: BatchUpdateEntry[];
  }>): Promise<BatchUpdateMultipleResult[]> {
    await this.initialize();
    this.ensureAuthorized();

    const promises = updates.map(async (sheetUpdate): Promise<BatchUpdateMultipleResult> => {
      try {
        if (!sheetUpdate.updates.length) {
          return {
            spreadsheetId: sheetUpdate.spreadsheetId,
            success: true,
            updatedCells: 0
          };
        }

        await this.retryOperation(async () => {
          const data = sheetUpdate.updates.map(update => {
            let column = '';
            let colNum = update.col;

            while (colNum >= 0) {
              column = String.fromCharCode(65 + (colNum % 26)) + column;
              colNum = Math.floor(colNum / 26) - 1;
            }

            const range = this.formatRange(sheetUpdate.sheetName, `${column}${update.row}`);
            return {
              range,
              values: [[update.value]]
            };
          });

          await gapi.client.sheets.spreadsheets.values.batchUpdate({
            spreadsheetId: sheetUpdate.spreadsheetId,
            resource: {
              valueInputOption: 'USER_ENTERED',
              data
            }
          });
        });

        return {
          spreadsheetId: sheetUpdate.spreadsheetId,
          success: true,
          updatedCells: sheetUpdate.updates.length
        };
      } catch (error) {
        return {
          spreadsheetId: sheetUpdate.spreadsheetId,
          success: false,
          updatedCells: 0,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    });

    return Promise.all(promises);
  }

  // === PERMISSION CACHING METHODS ===

  /**
   * Clear permission cache for specific spreadsheet or all
   */
  clearPermissionCache(spreadsheetId?: string): void {
    if (spreadsheetId) {
      this.permissionCache.delete(this.getCacheKey(spreadsheetId));
    } else {
      this.permissionCache.clear();
    }
  }

  /**
   * Get cached permission results
   */
  getCachedPermissions(): Array<{ spreadsheetId: string; permissions: PermissionCheckResult; cached: Date }> {
    const results: Array<{ spreadsheetId: string; permissions: PermissionCheckResult; cached: Date }> = [];

    this.permissionCache.forEach((entry) => {
      if (this.isCacheValid(entry)) {
        results.push({
          spreadsheetId: entry.result.spreadsheetId,
          permissions: entry.result,
          cached: new Date(entry.timestamp)
        });
      }
    });

    return results;
  }

  // === SHEET METADATA METHODS ===

  /**
   * Get sheet metadata including title, last modified, and sheet list
   */
  async getSheetMetadata(spreadsheetId: string): Promise<SheetMetadata> {
    await this.initialize();
    this.ensureAuthorized();

    const response = await this.retryOperation(async () => {
      return gapi.client.sheets.spreadsheets.get({
        spreadsheetId,
        fields: 'properties(title),sheets(properties(sheetId,title,index))'
      });
    });

    const spreadsheet = response.result;
    const sheets = (spreadsheet.sheets || []).map((sheet: any) => ({
      sheetId: sheet.properties.sheetId,
      title: sheet.properties.title,
      index: sheet.properties.index
    }));

    return {
      spreadsheetId,
      title: spreadsheet.properties?.title || 'Untitled',
      lastModified: new Date().toISOString(), // Google Sheets API doesn't provide this in v4
      sheetCount: sheets.length,
      sheets: sheets.sort((a: any, b: any) => a.index - b.index)
    };
  }

  /**
   * Verify that a specific sheet exists within a spreadsheet
   */
  async validateSheetExists(spreadsheetId: string, sheetName: string): Promise<boolean> {
    try {
      const metadata = await this.getSheetMetadata(spreadsheetId);
      return metadata.sheets.some(sheet => sheet.title === sheetName);
    } catch (error) {
      return false;
    }
  }

  /**
   * Batch update a specific sheet - used by TableSyncManager
   */
  async batchUpdateSheet(spreadsheetId: string, sheetName: string, updates: BatchUpdateEntry[]): Promise<void> {
    if (!updates.length) {
      return;
    }

    await this.initialize();
    this.ensureAuthorized();

    const data = updates.map(update => {
      let column = '';
      let colNum = update.col;

      while (colNum >= 0) {
        column = String.fromCharCode(65 + (colNum % 26)) + column;
        colNum = Math.floor(colNum / 26) - 1;
      }

      const range = this.formatRange(sheetName, `${column}${update.row}`);
      return {
        range,
        values: [[update.value]]
      };
    });

    await gapi.client.sheets.spreadsheets.values.batchUpdate({
      spreadsheetId,
      resource: {
        valueInputOption: 'USER_ENTERED',
        data
      }
    });
  }

  /**
   * Append rows to a sheet - used for adding new references
   */
  async appendRows(spreadsheetId: string, sheetName: string, rows: string[][]): Promise<void> {
    if (!rows.length) {
      return;
    }

    await this.initialize();
    this.ensureAuthorized();

    await this.retryOperation(async () => {
      await gapi.client.sheets.spreadsheets.values.append({
        spreadsheetId,
        range: this.formatRange(sheetName, 'A1'),
        valueInputOption: 'USER_ENTERED',
        insertDataOption: 'INSERT_ROWS',
        resource: {
          values: rows
        }
      });
    });
  }

  /**
   * Delete a row from a sheet by row index (0-based, but header is row 0)
   * @param spreadsheetId - The spreadsheet ID
   * @param sheetName - The sheet name
   * @param rowIndex - The row index to delete (0-based, where header is 0)
   */
  async deleteRow(spreadsheetId: string, sheetName: string, rowIndex: number): Promise<void> {
    await this.initialize();
    this.ensureAuthorized();

    // Get sheet metadata to find the sheetId
    const metadata = await this.getSheetMetadata(spreadsheetId);
    const sheet = metadata.sheets.find(s => s.title === sheetName);

    if (!sheet) {
      throw new Error(`Sheet "${sheetName}" not found in spreadsheet`);
    }

    await this.retryOperation(async () => {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests: [{
            deleteDimension: {
              range: {
                sheetId: sheet.sheetId,
                dimension: 'ROWS',
                startIndex: rowIndex,
                endIndex: rowIndex + 1
              }
            }
          }]
        }
      });
    });
  }
}

export const googleSheetsService = new GoogleSheetsService();
